import { DownloadIcon } from '@chakra-ui/icons';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Box,
  DrawerFooter,
  Button,
  VStack,
  Image,
  Center,
  useToast,
  Portal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  Modal,
  useDisclosure,
  Icon,
  ModalBody,
} from '@chakra-ui/react';
import { Api } from 'api';
import { LoadingText } from 'components';
import { createCustomerSocket, getExpertiseBackroundImageUrl } from 'core';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  BodyPartAnswerInfo,
  BodyPartExpertiseInfo,
  CheckOldBodyDamagesResponse,
  ExpertiseBodyDamagePartType,
  // ExpertiseBodyDamageType,
  ExpertiseChassisCarBodyPartName,
  ExpertiseCarBodyTypes,
  ExpertiseChassisDamageType,
  VehicleQueryCacheKeys,
} from 'types';
// import { ExpertiseBodyworkExpertiseStateSelector } from './ExpertiseBodyworkExpertiseStateSelector';
import { Socket } from 'socket.io-client';
import { Trans } from '@lingui/macro';
import { ExpertiseBodyworkExpertiseChassisStateSelector } from './ExpertiseBodyworkExpertiseChassisStateSelector';

export interface ExpertiseBodyworkExpertiseChassisDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  carBodyType: ExpertiseCarBodyTypes;
  isDisabled?: boolean;
}

type ExpertisePartsData = {
  [key: number]: ExpertiseChassisDamageType;
};

type ExpertiseQuestionData = {
  [key: number]: string;
};

type ExpertiseSaveData = { expertises: ExpertisePartsData; answers: ExpertiseQuestionData };

type PositionLookupTableRow = {
  left?: string;
  bottom?: string;
  top?: string;
  right?: string;
};

type PositionLookupTable = {
  [key in ExpertiseChassisCarBodyPartName]: PositionLookupTableRow;
};

const genericPositionLookupTable: PositionLookupTable = {
  [ExpertiseChassisCarBodyPartName.SagOnSasi]: {
    top: '0%',
    right: '12%',
  },
  [ExpertiseChassisCarBodyPartName.SagPodye]: {
    top: '16%',
    right: '21%',
  },

  [ExpertiseChassisCarBodyPartName.SagKule]: {
    top: '8%',
    right: '19%',
  },

  [ExpertiseChassisCarBodyPartName.SagOnDirek]: {
    top: '25%',
    right: '13%',
  },
  [ExpertiseChassisCarBodyPartName.SagUstDirek]: {
    top: '30%',
    right: '27%',
  },
  [ExpertiseChassisCarBodyPartName.SagOrtaDirek]: {
    top: '48%',
    right: '13%',
  },
  [ExpertiseChassisCarBodyPartName.SagArkaCamurlukKilitKarsiligi]: {
    top: '65%',
    right: '10%',
  },
  [ExpertiseChassisCarBodyPartName.SagArkaPodye]: {
    bottom: '20%',
    right: '25%',
  },

  [ExpertiseChassisCarBodyPartName.SagArkaSasi]: {
    bottom: '2%',
    right: '12%',
  },
  [ExpertiseChassisCarBodyPartName.OnPanel]: {
    top: '2%',
    right: '46%',
  },
  [ExpertiseChassisCarBodyPartName.BagajHavuzu]: {
    bottom: '12%',
    right: '46%',
  },
  [ExpertiseChassisCarBodyPartName.ArkaPanel]: {
    bottom: '1%',
    right: '46%',
  },
  [ExpertiseChassisCarBodyPartName.SolOnSasi]: {
    top: '0%',
    left: '12%',
  },
  [ExpertiseChassisCarBodyPartName.SolPodye]: {
    top: '16%',
    left: '21%',
  },
  [ExpertiseChassisCarBodyPartName.SolKule]: {
    top: '8%',
    left: '19%',
  },
  [ExpertiseChassisCarBodyPartName.SolOnDirek]: {
    top: '25%',
    left: '13%',
  },
  [ExpertiseChassisCarBodyPartName.SolUstDirek]: {
    top: '30%',
    left: '27%',
  },
  [ExpertiseChassisCarBodyPartName.SolOrtaDirek]: {
    top: '48%',
    left: '13%',
  },
  [ExpertiseChassisCarBodyPartName.SolArkaCamurlukKilitKarsiligi]: {
    top: '65%',
    left: '10%',
  },
  [ExpertiseChassisCarBodyPartName.SolArkaPodye]: {
    bottom: '20%',
    left: '25%',
  },
  [ExpertiseChassisCarBodyPartName.SolArkaSasi]: {
    bottom: '2%',
    left: '12%',
  },
};

const VehicleTypePositionTable: {
  [key in ExpertiseCarBodyTypes]: PositionLookupTable;
} = {
  [ExpertiseCarBodyTypes.Sedan]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Hatchback5]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Hatchback2]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Pickup]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Pickup2]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Minibus]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Panelvan]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.CanliPanelvan]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.TekSurguPanelvan]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.SUV]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Station]: genericPositionLookupTable,
};

export const ExpertiseBodyworkExpertiseChassisDrawer: React.FC<ExpertiseBodyworkExpertiseChassisDrawerProps> = ({
  isOpen,
  onClose,
  carBodyType,
  isDisabled,
}) => {
  const customer = useSelector((selector: RootState) => selector.customer.focusedCustomer);
  const inputPositionTable = useMemo(() => VehicleTypePositionTable[carBodyType], [carBodyType]);
  const toast = useToast();
  const [oldReportUrl, setOldReportUrl] = useState('');
  const {
    isOpen: checkOldDamagesModalIsOpen,
    onOpen: checkOldDamagesModalOnOpen,
    onClose: checkOldDamagesModalOnClose,
  } = useDisclosure();
  const {
    data: expertises,
    isLoading: isExpertisesLoading,
    refetch: refetchExpertises,
  } = useQuery(
    [VehicleQueryCacheKeys.GetChassisControl, customer!.id],
    async () => {
      const { data: expertiseResponse } = await Api.Expertise.getCustomerBodyPartsExpertise(
        customer!.id,
        ExpertiseBodyDamagePartType.Chassis,
      );
      const { expertiseBodyDamages, expertiseBodyAnswers } = expertiseResponse;

      return {
        expertises: expertiseBodyDamages.reduce((accum: any, nextValue: BodyPartExpertiseInfo) => {
          // eslint-disable-next-line no-param-reassign
          accum[nextValue.expertiseBodyDamagePartId] = nextValue.expertiseBodyDamageValue;
          return accum;
        }, {} as ExpertisePartsData),
        answers: expertiseBodyAnswers.reduce((accum: any, nextValue: BodyPartAnswerInfo) => {
          // eslint-disable-next-line no-param-reassign
          accum[nextValue.expertiseBodyQuestionId] = nextValue.answer;
          return accum;
        }, {} as ExpertiseQuestionData),
      };
    },
    {
      refetchOnWindowFocus: false,
      enabled: customer != null && isOpen,
      cacheTime: 0,
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Boyalı Değişen verileri çekilirken hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const {
    data: expertiseTemplates,
    refetch: expertisesTemplateRefetch,
    isLoading: isExpertisesTemplateLoading,
  } = useQuery(
    VehicleQueryCacheKeys.GetChassisControlExpertiseTemplates,
    async () => {
      const response = await Api.Expertise.getExpertiseTemplate(
        customer!.customerVehicle!.vehicleTypeId,
        ExpertiseBodyDamagePartType.Chassis,
      );
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: customer != null && isOpen,
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Boyalı değişen işlemleri için gereken verileri çekerken hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      answers: (expertises?.answers || {}) as ExpertiseQuestionData,
      expertises: (expertises?.expertises || {}) as ExpertisePartsData,
    },
    onSubmit: async (data: ExpertiseSaveData) => {
      if (expertiseTemplates) {
        const filledExpertises = expertiseTemplates.expertiseBodyDamageParts.reduce((accum, next) => {
          // eslint-disable-next-line no-param-reassign
          accum[next.id] = data.expertises[next.id] || ExpertiseChassisDamageType.Original;
          return accum;
        }, {} as any);

        const filledAnswers = expertiseTemplates.expertiseBodyQuestions.reduce((accum, next) => {
          // eslint-disable-next-line no-param-reassign
          accum[next.id] = data.answers[next.id] || '0';
          return accum;
        }, {} as any);

        if (checkOldDamagesModalIsOpen) {
          checkOldDamagesModalOnClose();
          // eslint-disable-next-line no-use-before-define
          saveExpertiseMutate.mutateAsync({ expertises: filledExpertises, answers: filledAnswers });
        } else {
          // eslint-disable-next-line no-use-before-define
          const oldResults = await checkOldDamageMutate.mutateAsync({
            expertises: filledExpertises,
            answers: filledAnswers,
          });
          if (!oldResults.hasInconsistency) {
            // eslint-disable-next-line no-use-before-define
            saveExpertiseMutate.mutateAsync({ expertises: filledExpertises, answers: filledAnswers });
          } else {
            setOldReportUrl(oldResults.oldReportUrl!);
            checkOldDamagesModalOnOpen();
          }
        }
      }
    },
  });
  const saveExpertiseMutate = useMutation<void, any, ExpertiseSaveData>(
    async (data: ExpertiseSaveData) => {
      await Api.Expertise.saveExpertiseBodyParts({
        customerId: customer!.id,
        expertiseBodyDamagePartTypeId: ExpertiseBodyDamagePartType.Chassis,
        bodyDamageAnswers: Object.entries(data.answers).map(([key, value]) => {
          return {
            answer: value,
            expertiseBodyQuestionId: parseInt(key, 10),
          };
        }),
        bodyDamageParts: Object.entries(data.expertises).map(([key, value]) => {
          return {
            bodyDamageValue: value,
            expertiseBodyDamagePartId: parseInt(key, 10),
          };
        }),
      });
    },
    {
      onSuccess: () => {
        onClose();
        toast({
          title: 'Başarı ile kayıt edildi',
          status: 'success',
          isClosable: true,
        });
      },
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Kayıt sırasında hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const checkOldDamageMutate = useMutation<CheckOldBodyDamagesResponse, any, ExpertiseSaveData>(
    async (data: ExpertiseSaveData) => {
      const response = await Api.Expertise.checkOldBodyDamages({
        customerId: customer!.id,
        expertiseBodyDamagePartTypeId: ExpertiseBodyDamagePartType.Chassis,
        bodyDamageAnswers: Object.entries(data.answers).map(([key, value]) => {
          return {
            answer: value,
            expertiseBodyQuestionId: parseInt(key, 10),
          };
        }),
        bodyDamageParts: Object.entries(data.expertises).map(([key, value]) => {
          return {
            bodyDamageValue: value,
            expertiseBodyDamagePartId: parseInt(key, 10),
          };
        }),
      });
      return response.data;
    },
    {
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Kontrol sırasında hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );
  const { setFieldValue, values, handleSubmit } = formik;
  const onBodyworkExpertiseChanged = (
    expertiseKeyName: ExpertiseChassisCarBodyPartName,
    expertiseBodyworkExpertiseState: ExpertiseChassisDamageType,
  ) => {
    setFieldValue(`expertises.${expertiseKeyName}`, expertiseBodyworkExpertiseState);
  };

  const expertiseImageUrl = getExpertiseBackroundImageUrl(carBodyType);

  const [customerSocket, setCustomerSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (customer && isOpen) {
      const newCustomerSocket = createCustomerSocket(customer.id.toString());
      setCustomerSocket(newCustomerSocket);

      const handleExpertiseBodyDamageChanged = (data: any) => {
        if (data.customer.id === customer?.id) {
          refetchExpertises();
        }
      };

      newCustomerSocket.on('expertiseBodyDamageChanged', handleExpertiseBodyDamageChanged);

      return () => {
        newCustomerSocket.off('expertiseBodyDamageChanged', handleExpertiseBodyDamageChanged);
        newCustomerSocket.disconnect();
      };
    }
    return () => {
      if (customerSocket) {
        customerSocket.disconnect();
      }
    };
  }, [customer, isOpen, refetchExpertises]);

  useEffect(() => {
    refetchExpertises();
    expertisesTemplateRefetch();
  }, [isOpen, refetchExpertises, expertisesTemplateRefetch]);

  const showLoading = !customer || !expertiseTemplates || isExpertisesTemplateLoading || isExpertisesLoading || !isOpen;

  return (
    <Portal>
      <Modal isOpen={checkOldDamagesModalIsOpen} onClose={checkOldDamagesModalOnClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Uyarı</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Trans>Geçmiş ekspertiz raporuna göre boya değişenlerde değişiklik tespit edilmiştir.</Trans>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Button
              leftIcon={<Icon as={DownloadIcon} />}
              colorScheme="teal"
              onClick={() => {
                window.open(oldReportUrl);
              }}
            >
              <Trans>Geçmiş Ekspertiz Raporu</Trans>
            </Button>
            <Button
              onClick={() => {
                handleSubmit();
              }}
            >
              <Trans>Kaydet</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Drawer isOpen={isOpen} onClose={onClose} size="xl">
        <DrawerOverlay />
        <DrawerContent overflow="auto">
          <form noValidate onSubmit={handleSubmit}>
            <DrawerCloseButton disabled={showLoading || saveExpertiseMutate.isLoading} />
            <DrawerHeader>
              <Trans>Direk ve Şasi Kontrolleri</Trans>
            </DrawerHeader>

            <DrawerBody height="full">
              {showLoading ? (
                <Center>
                  <LoadingText />
                </Center>
              ) : (
                <VStack gap={24} align="stretch">
                  <VStack>
                    <Box width="100%" position="relative" maxWidth="450px">
                      <Image src={expertiseImageUrl} fit="fill" width="100%" />
                      <Box position="absolute" top="0" left="0" right="0" bottom="0">
                        {expertiseTemplates.expertiseBodyDamageParts.map((expertiseTemplate) => {
                          const partInfo =
                            inputPositionTable[expertiseTemplate.displayId as ExpertiseChassisCarBodyPartName];
                          return (
                            <ExpertiseBodyworkExpertiseChassisStateSelector
                              key={`ebw${expertiseTemplate.displayId}`}
                              expertiseKeyName={expertiseTemplate.id}
                              displayId={expertiseTemplate.displayId}
                              onBodyworkExpertiseChanged={onBodyworkExpertiseChanged}
                              value={values.expertises[expertiseTemplate.id] || ExpertiseChassisDamageType.Original}
                              left={partInfo?.left}
                              top={partInfo?.top}
                              bottom={partInfo?.bottom}
                              right={partInfo?.right}
                              label={expertiseTemplate.name}
                              isDisabled={isDisabled}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  </VStack>
                </VStack>
              )}
            </DrawerBody>

            <DrawerFooter>
              <Button
                variant="outline"
                mr={3}
                onClick={onClose}
                disabled={showLoading || saveExpertiseMutate.isLoading}
              >
                <Trans>İptal</Trans>
              </Button>
              {!isDisabled && (
                <Button type="submit" disabled={showLoading || saveExpertiseMutate.isLoading}>
                  <Trans>Kaydet</Trans>
                </Button>
              )}
            </DrawerFooter>
          </form>
        </DrawerContent>
      </Drawer>
    </Portal>
  );
};
